// ============================================================
//  ALTIMO Medical Center — OrdenesLabPanel.jsx
//  Panel compartido: el médico solicita un examen desde el
//  expediente o desde Formularios Clínicos. NO cobra a la
//  cuenta — eso ocurre cuando Laboratorio sube el resultado
//  (lab:subir con orden_id → ordenes_lab queda 'Completada').
//  Reutilizado por: ExpedientePaciente.jsx, Formularios.jsx
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';
import { useConfirmar } from './ModalConfirmar';

const S = {
  card:   { background: '#fff', borderRadius: '10px', border: '1px solid #dde3ea', padding: '18px', marginBottom: '14px' },
  fila2:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  titulo: {
    fontSize: '12px', fontWeight: '700', color: '#1B2A5E',
    textTransform: 'uppercase', letterSpacing: '.05em',
    marginBottom: '14px', paddingBottom: '10px', borderBottom: '2px solid #e8f0fb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  input:  { width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #c0ccda', borderRadius: '7px', background: '#fff', color: '#333', outline: 'none', boxSizing: 'border-box' },
  label:  { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.04em' },
  campo:  { marginBottom: '12px' },
  tabla:  { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:     { textAlign: 'left', padding: '8px 12px', fontSize: '11px', color: '#6c757d', fontWeight: '600', borderBottom: '2px solid #e8f0fb', background: '#f8fafd' },
  td:     { padding: '9px 12px', borderBottom: '1px solid #eef1f6', color: '#333' },
  btnPrimario:   { background: '#1B2A5E', color: '#fff', border: 'none', borderRadius: '7px', padding: '8px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  btnVerde:      { background: '#0c8599', color: '#fff', border: 'none', borderRadius: '7px', padding: '7px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  btnSecundario: { background: '#fff', color: '#495057', border: '1px solid #c0ccda', borderRadius: '7px', padding: '7px 14px', fontSize: '12px', cursor: 'pointer' },
  pill: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: '99px',
    fontSize: '10px', fontWeight: '700',
    background: { verde: '#d4edda', amber: '#fff3cd', azul: '#cce5ff', rojo: '#f8d7da', gris: '#e2e3e5' }[color] || '#e2e3e5',
    color:      { verde: '#155724', amber: '#856404', azul: '#004085', rojo: '#721c24', gris: '#383d41' }[color] || '#333',
  }),
};

const ESTADO_ORDEN = {
  Pendiente:   { color:'amber', label:'⏳ Pendiente de tomar' },
  En_Proceso:  { color:'azul',  label:'🧪 En proceso'         },
  Completada:  { color:'verde', label:'✔ Resultado disponible' },
  Cancelada:   { color:'rojo',  label:'✕ Cancelada'           },
};

const INTERPRETACION_PILL = {
  Normal:      { color:'verde', label:'✅ Normal'     },
  Alterado:    { color:'amber', label:'⚠️ Alterado'   },
  Critico:     { color:'rojo',  label:'🚨 Crítico'    },
  Inconcluso:  { color:'gris',  label:'❓ Inconcluso' },
  Invalido:    { color:'gris',  label:'🚫 Inválido'   },
};

// Props:
//  admision_id  (requerido) — id de la admisión activa
//  medico_id    (opcional)  — médico tratante de esa admisión, para prellenar
//  usuario      (requerido) — usuario logueado (quien registra la acción)
export default function OrdenesLabPanel({ admision_id, medico_id, usuario }) {
  const [ordenes,      setOrdenes]      = useState([]);
  const [cargando,     setCargando]     = useState(true);
  const [mostrarForm,  setMostrarForm]  = useState(false);

  // ── Formulario nueva orden
  const [busqTarifa,   setBusqTarifa]   = useState('');
  const [resTarifa,    setResTarifa]    = useState([]);
  const [tarifaSel,    setTarifaSel]    = useState(null);
  const [examenLibre,  setExamenLibre]  = useState('');
  const [indicaciones, setIndicaciones] = useState('');
  const [guardando,    setGuardando]    = useState(false);
  const [error,        setError]        = useState('');
  const { confirmar, ModalConfirmarUI } = useConfirmar();

  const cargarOrdenes = useCallback(async () => {
    if (!admision_id) { setCargando(false); return; }
    setCargando(true);
    try {
      const r = await api.ordenesLab.listar({ admision_id });
      if (r?.ok) setOrdenes(r.data);
    } catch(e) { console.warn('[ordenes_lab] Error cargando:', e.message); }
    setCargando(false);
  }, [admision_id]);

  useEffect(() => { cargarOrdenes(); }, [cargarOrdenes]);

  // Búsqueda en tarifario (categoría "Examen"/"Laboratorio" igual que ya usa Laboratorio.jsx)
  useEffect(() => {
    if (busqTarifa.trim().length < 2) { setResTarifa([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.tarifario.buscar(busqTarifa);
        if (r?.ok) setResTarifa(r.data.slice(0, 10));
      } catch(_) { setResTarifa([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [busqTarifa]);

  const seleccionarTarifa = (t) => {
    setTarifaSel(t);
    setBusqTarifa(t.nombre);
    setResTarifa([]);
  };

  const limpiarForm = () => {
    setBusqTarifa(''); setTarifaSel(null); setExamenLibre(''); setIndicaciones(''); setError('');
  };

  const guardarOrden = async () => {
    setError('');
    if (!admision_id) { setError('No hay admisión activa para este paciente.'); return; }
    const nombreExamen = tarifaSel ? tarifaSel.nombre : examenLibre.trim();
    if (!nombreExamen) { setError('Indica el examen a solicitar (búscalo en el tarifario o descríbelo).'); return; }
    if (!indicaciones.trim()) { setError('Las indicaciones / motivo clínico del estudio son obligatorias.'); return; }

    setGuardando(true);
    try {
      const r = await api.ordenesLab.crear({
        admision_id,
        medico_id:    medico_id || null,
        tipo_examen:  nombreExamen,
        tarifa_id:    tarifaSel ? tarifaSel.id : null,
        indicaciones: indicaciones.trim(),
        usuario_id:   usuario.id,
      });
      if (r?.ok) {
        limpiarForm();
        setMostrarForm(false);
        cargarOrdenes();
      } else {
        setError(r?.error || 'No se pudo guardar la orden.');
      }
    } catch(e) { setError(e.message); }
    setGuardando(false);
  };

  const cancelarOrden = async (o) => {
    if (!(await confirmar({
      texto: `¿Cancelar la orden de "${o.tipo_examen}"? Aún no tiene resultado cargado.`,
      tipo: 'advertencia',
      textoConfirmar: 'Cancelar orden',
    }))) return;
    const r = await api.ordenesLab.cancelar({
      id: o.id, motivo: 'Cancelada por el médico', usuario_id: usuario.id,
    });
    if (r?.ok) cargarOrdenes();
    else alert(r?.error || 'No se pudo cancelar.');
  };

  // Marca (o desmarca) un resultado como revisado por el médico. Se llama
  // automáticamente al abrir el archivo ("Abrir resultado") y también puede
  // invocarse manualmente con el botón correspondiente. Actualiza el estado
  // local de inmediato (optimista) y además recarga desde servidor.
  const marcarRevisado = async (ordenId, marcar) => {
    setOrdenes(prev => prev.map(o => o.id === ordenId
      ? { ...o, revisado_en: marcar ? new Date().toISOString() : null }
      : o));
    try {
      const r = await api.dashboard.medicoMarcarLabRevisado({
        orden_id: ordenId, usuario_id: usuario?.id, marcar,
      });
      if (!r?.ok) cargarOrdenes(); // revertir si falló
    } catch (e) {
      console.warn('[ordenes_lab] Error marcando revisado:', e.message);
      cargarOrdenes();
    }
  };

  if (!admision_id) {
    return (
      <div style={S.card}>
        <div style={S.titulo}><span>🧪 Estudios Clínicos</span></div>
        <div style={{ color:'#adb5bd', fontSize:'13px', padding:'12px 0', textAlign:'center' }}>
          Este paciente no tiene una admisión activa. Solo se pueden solicitar exámenes a pacientes hospitalizados.
        </div>
      </div>
    );
  }

  return (
    <div style={S.card}>
      {ModalConfirmarUI}
      <div style={S.titulo}>
        <span>🧪 Estudios Clínicos ({ordenes.length})</span>
        <button style={S.btnVerde} onClick={() => setMostrarForm(v => !v)}>
          {mostrarForm ? 'Cerrar' : '+ Solicitar examen'}
        </button>
      </div>

      {/* ── Formulario nueva orden ── */}
      {mostrarForm && (
        <div style={{ background:'#f8fafd', border:'1px solid #dde3ea', borderRadius:'8px', padding:'14px', marginBottom:'16px' }}>
          <div style={S.campo}>
            <label style={S.label}>Examen (buscar en tarifario) *</label>
            <input style={S.input} placeholder="Ej: Hemograma, Glucemia, Radiografía..."
              value={busqTarifa} onChange={e => { setBusqTarifa(e.target.value); setTarifaSel(null); }} />
            {resTarifa.length > 0 && (
              <div style={{ border:'1px solid #dde3ea', borderRadius:'7px', marginTop:'4px', maxHeight:'180px', overflowY:'auto' }}>
                {resTarifa.map(t => (
                  <div key={t.id} onClick={() => seleccionarTarifa(t)}
                    style={{ padding:'8px 10px', cursor:'pointer', fontSize:'12px', borderBottom:'1px solid #eef1f6',
                      display:'flex', justifyContent:'space-between' }}
                    onMouseEnter={e => e.currentTarget.style.background='#f0f7ff'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    <span>{t.nombre}</span>
                    <span style={{ color:'#6c757d' }}>${Number(t.precio).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
            {tarifaSel ? (
              <div style={{ fontSize:'11px', color:'#0c8599', marginTop:'4px', fontWeight:600 }}>
                ✔ Seleccionado: {tarifaSel.nombre} — ${Number(tarifaSel.precio).toFixed(2)} (se cobrará al completar el resultado)
              </div>
            ) : busqTarifa.trim().length >= 2 && resTarifa.length === 0 && (
              <div style={{ marginTop:'8px' }}>
                <label style={S.label}>No está en el tarifario — describe el examen manualmente</label>
                <input style={S.input} placeholder="Nombre del examen..."
                  value={examenLibre} onChange={e => setExamenLibre(e.target.value)} />
                <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'4px' }}>
                  Sin tarifa asociada, este examen no generará cargo automático a la cuenta.
                </div>
              </div>
            )}
          </div>

          <div style={S.campo}>
            <label style={S.label}>Indicaciones / motivo clínico *</label>
            <input style={S.input} placeholder="Ej: control postoperatorio, sospecha de infección..."
              value={indicaciones} onChange={e => setIndicaciones(e.target.value)} />
          </div>

          {error && (
            <div style={{ color:'#dc3545', fontSize:'12px', fontWeight:600, marginTop:'6px' }}>⚠ {error}</div>
          )}

          <button style={{ ...S.btnPrimario, marginTop:'10px', opacity: guardando ? 0.6 : 1 }}
            onClick={guardarOrden} disabled={guardando}>
            {guardando ? 'Guardando...' : '✔ Enviar orden a Laboratorio'}
          </button>
        </div>
      )}

      {/* ── Lista de órdenes ── */}
      {cargando ? (
        <div style={{ textAlign:'center', padding:'20px', color:'#6c757d' }}>Cargando...</div>
      ) : ordenes.length === 0 ? (
        <div style={{ color:'#adb5bd', fontSize:'13px', padding:'12px 0', textAlign:'center' }}>
          No hay estudios clínicos registrados para esta admisión.
        </div>
      ) : (
        <table style={S.tabla}>
          <thead>
            <tr>
              {['Fecha','Examen','Indicaciones','Estado','Costo','Resultado',''].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ordenes.map(o => {
              const est = ESTADO_ORDEN[o.estado] || { color:'gris', label:o.estado };
              return (
                <tr key={o.id}>
                  <td style={{ ...S.td, fontSize:'12px', whiteSpace:'nowrap' }}>
                    {new Date(o.creado_en).toLocaleString('es-SV', { dateStyle:'short', timeStyle:'short' })}
                  </td>
                  <td style={S.td}>{o.tipo_examen}</td>
                  <td style={{ ...S.td, fontSize:'11px', color:'#6c757d' }}>{o.indicaciones || '—'}</td>
                  <td style={S.td}><span style={S.pill(est.color)}>{est.label}</span></td>
                  <td style={{ ...S.td, textAlign:'center' }}>
                    {o.tarifa_precio ? `$${Number(o.tarifa_precio).toFixed(2)}` : '—'}
                  </td>
                  <td style={S.td}>
                    {o.estado === 'Completada' && o.resultado_ruta ? (
                      <div>
                        <div style={{ fontSize:'11px', color:'#6c757d' }}>
                          {o.resultado_fecha && new Date(o.resultado_fecha).toLocaleString('es-SV', { dateStyle:'short', timeStyle:'short' })}
                        </div>
                        {o.resultado_interpretacion && (
                          <div style={{ marginTop:'3px' }}>
                            <span style={S.pill((INTERPRETACION_PILL[o.resultado_interpretacion] || {}).color || 'gris')}>
                              {(INTERPRETACION_PILL[o.resultado_interpretacion] || {}).label || o.resultado_interpretacion}
                            </span>
                          </div>
                        )}
                        {o.resultado_notas && (
                          <div style={{ fontSize:'11px', color:'#475569', fontStyle:'italic', maxWidth:'160px', marginTop:'3px' }}>
                            "{o.resultado_notas}"
                          </div>
                        )}
                        <div style={{ display:'flex', alignItems:'center', gap:'6px', marginTop:'4px', flexWrap:'wrap' }}>
                          <button style={{ ...S.btnVerde, fontSize:'11px', padding:'4px 10px' }}
                            onClick={() => {
                              // Antes: window.altimo.laboratorio.abrir({ ruta }) usaba
                              // shell.openPath() de Electron. Ahora el resultado ya
                              // viene como URL pública del servidor; se abre en una
                              // pestaña nueva del navegador.
                              window.open(api.lab.urlArchivo(o.resultado_ruta), '_blank');
                              if (!o.revisado_en) marcarRevisado(o.id, true);
                            }}>
                            👁 Abrir resultado
                          </button>
                          {o.revisado_en ? (
                            <span title={`Revisado el ${new Date(o.revisado_en).toLocaleString('es-SV', { dateStyle:'short', timeStyle:'short' })}`}
                              style={{ ...S.pill('verde'), cursor:'pointer' }}
                              onClick={() => marcarRevisado(o.id, false)}>
                              ✓ Revisado
                            </span>
                          ) : (
                            <span style={{ ...S.pill('amber'), cursor:'pointer' }}
                              onClick={() => marcarRevisado(o.id, true)}>
                              ○ Marcar revisado
                            </span>
                          )}
                        </div>
                      </div>
                    ) : o.estado === 'Completada' ? (
                      <span style={{ fontSize:'11px', color:'#adb5bd' }}>Archivo no disponible</span>
                    ) : (
                      <span style={{ fontSize:'11px', color:'#adb5bd' }}>—</span>
                    )}
                  </td>
                  <td style={S.td}>
                    {o.estado === 'Pendiente' && (
                      <button style={{ ...S.btnSecundario, fontSize:'11px', padding:'4px 10px', color:'#dc3545' }}
                        onClick={() => cancelarOrden(o)}>
                        Cancelar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
